import { bindable, computedFrom } from 'aurelia-framework';
/**
 * Allows the user to see, filter and select a product.
 */
class ProductSelector {
  /**
   * The list of available products for the user.
   * @type {Array}
   */
  @bindable products = [];
  /**
   * The name of the user, to show on the view.
   * @type {String}
   */
  @bindable username = '';
  /**
   * Whether or not the list of products is being requested.
   * @type {Boolean}
   */
  @bindable loading = false;
  /**
   * Whether or not there was an error while getting the list of products.
   * @type {Boolean}
   */
  @bindable hasError = false;
  /**
   * The filtered list of products the view renders.
   * @type {Array}
   */
  productsList = [];
  /**
   * The text of the search box.
   * @type {String}
   */
  searchText = '';
  /**
   * A normalized version of the text of the search box.
   * @type {String}
   * @access protected
   * @ignore
   */
  _searchQuery = '';
  /**
   * The list of products, formatted with the text for the avatar and the
   * properties the controller will use to filter them based on the search box
   * text.
   * @type {Array}
   */
  _productsFormatted = [];
  /**
   * This is called every time the list of products changes. This method formats
   * the list and calls the method that filters it.
   * @param {Array} products The new list of products.
   */
  productsChanged(products) {
    // Just format them if there are actual products.
    if (products && products.length) {
      this._productsFormatted = products.map((product) => {
        // Create an avatar with the first letter of each word on the product name.
        const avatar = product.title
        .split(' ')
        .map((word) => word.substr(0, 1))
        .join('');

        /**
         * Normalize the name and the avatar and add them to the properties the method
         * that filters te products can use to see if there are matches with the
         * search text.
         */
        const searchProps = {
          name: product.title.toLowerCase(),
          avatar: avatar.toLowerCase(),
        };

        return Object.assign({}, product, {
          avatar,
          searchProps,
        });
      });
    } else {
      this._productsFormatted = [];
    }

    this._searchProducts();
  }
  /**
   * This is called every time the search box text changes. It saves the text, the
   * normalized version and calls the method that filters the products.
   * @param {String} text The new text.
   */
  search(text) {
    this.searchText = text;
    this._searchQuery = text.toLowerCase();
    this._searchProducts();
  }
  /**
   * Gets the display options for the different parts of the component.
   * @type {Object}
   * @property {Boolean} products Whether or not to show the products list.
   * @property {Boolean} empty    Whether or not to show the message that says that
   *                              the user doesn't have any product assigned.
   * @property {Boolean} error    Whether or not to show the error message that says
   *                              that the list of products couldn't be retrieved.
   */
  @computedFrom('loading', 'showError', 'products')
  get display() {
    const result = {
      products: false,
      empty: false,
      error: false,
    };

    if (!this.loading) {
      result.products = !this.hasError && this.products.length > 0;
      result.empty = !this.hasError && this.products.length === 0;
      result.error = this.hasError;
    }

    return result;
  }
  /**
   * Creates the actual list the view will use to show the products. If there's a
   * search text, create a new list by filtering the formatted one; otherwise, just
   * copy it.
   * @access protected
   * @ignore
   */
  _searchProducts() {
    if (this._searchQuery) {
      this.productsList = this._productsFormatted
      .filter(({ searchProps }) => (
        searchProps.name.includes(this._searchQuery) ||
        searchProps.avatar.includes(this._searchQuery)
      ));
    } else {
      this.productsList = this._productsFormatted.slice();
    }
  }
}

export { ProductSelector };
